import { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import {
  cn,
  isPopulatedArray,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@win-reality/react-ui";
import { Link } from "react-router-dom";

import { useMachinesAsOf } from "@/hooks/useComplexes";
import { ROUTES } from "@/lib/routes";
import {
  getMachineHealth,
  getWorstHealth,
  MACHINE_HEALTH_DOT_CLASS,
} from "@/utils/machineHealth";

type ComplexHealthProps = {
  complexName: string;
  fields: MachinePublicHydrated[];
};

function ComplexHealth({ fields, complexName }: ComplexHealthProps) {
  const asOfMs = useMachinesAsOf();
  const fieldsWithIssues = fields.filter(
    (field) => getMachineHealth(field, asOfMs).state !== "green",
  );
  const fieldsWithIssuesCount = fieldsWithIssues.length;
  const someFieldNeedsAttention = fieldsWithIssuesCount > 0;
  const worstState = getWorstHealth(fields, asOfMs);

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-xs pr-2  uppercase tracking-wider text-white">
        Health
      </span>

      {someFieldNeedsAttention ? (
        <Popover>
          <PopoverTrigger asChild>
            <span
              className={cn(
                "flex items-center gap-2 cursor-pointer transition-all",
                fieldsWithIssuesCount > 0 && "p-2 rounded-full pr-3",
                "dark:hover:bg-white/20 light:hover:bg-black/10 ",
                "dark:text-white light:text-gray-500",
                "dark:bg-white/10 light:bg-black/5",
                "shrink-0",
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full dark:bg-white/20 light:bg-black/50 text-xs font-bold text-white">
                {fieldsWithIssuesCount}
              </span>
              <span className="text-xs">NEEDS ATTENTION</span>
              <span
                className={cn(
                  "inline-block h-3 w-3 rounded-full",
                  MACHINE_HEALTH_DOT_CLASS[worstState],
                )}
              />
            </span>
          </PopoverTrigger>
          <PopoverContent align="end" side="bottom">
            <div className="p-4 py-2">
              {isPopulatedArray(fields) ? (
                <div className="flex flex-col gap-0">
                  {fields.map((field) => {
                    const health = getMachineHealth(field, asOfMs);

                    const isLastItem =
                      field.machineUuid ===
                      fields[fields.length - 1].machineUuid;

                    const statusBadge = (
                      <span className="pl-2 uppercase text-xs text-gray-400 flex gap-2 items-center whitespace-nowrap">
                        {health.label}

                        <span
                          className={cn(
                            "inline-block h-3 w-3 rounded-full",
                            MACHINE_HEALTH_DOT_CLASS[health.state],
                          )}
                        />
                      </span>
                    );

                    return (
                      <div
                        key={field.machineUuid}
                        className={cn(
                          "flex items-center py-2 justify-between border-b border-gray-300",
                          isLastItem && "border-b-0",
                        )}
                      >
                        <Link
                          to={ROUTES.MANAGE_FIELD.replace(
                            ":complexName",
                            complexName,
                          ).replace(":fieldId", field.machineUuid)}
                          className="text-blue text-sm hover:underline"
                        >
                          {field.name}
                        </Link>

                        {health.tooltip ? (
                          <Tooltip content={health.tooltip} variant="dark">
                            {statusBadge}
                          </Tooltip>
                        ) : (
                          statusBadge
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-white">No fields available</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <span className="flex items-center  gap-2">
          <span className="text-xs dark:text-white light:text-gray-500">
            HEALTHY
          </span>
          <span className="inline-block h-3 w-3 rounded-full bg-green-500 shrink-0" />
        </span>
      )}
    </div>
  );
}

export { ComplexHealth };
